import React, { useRef, useEffect, useState } from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { SVGRenderer } from 'echarts/renderers';
import { GridComponent, TooltipComponent, TitleComponent, LegendComponent } from 'echarts/components';
// 标签自动布局、全局过渡动画等特性
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { deepCopy, isObject, throttle, yAxisFormatter } from '@lhb/func';
import { themeColor } from '../../config-v2';
import { EChartsOption } from 'echarts';
import styles from './index.module.less';
import { unstable_batchedUpdates } from 'react-dom';
import cs from 'classnames';

echarts.use([
  SVGRenderer,
  BarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  LabelLayout,
  UniversalTransition,
]);

interface V2ChartsTheme {
  color: string[],
  colorRGB: string[],
  colorGradient?: string[], // 漏斗图渐变需要
}

export interface V2BarChartProps {
  /**
   * @description 图表标题
   */
  title?: string;
  /**
   * @description 图表主题
   */
  theme?: 'blue' | 'green' | 'purple';
  /**
   * @description 自定义主题
   */
  customTheme?: V2ChartsTheme;
  /**
   * @description 图表宽度，默认不指定，跟随外容器
   */
  width?: number | string;
  /**
   * @description 图表高度
   * @default 270px
   */
  height?: number | string;
  /**
   * @description 是否需要边框
   */
  border?: boolean
  /**
   * @description 柱状图显示方向
   * @default vertical
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * @description 是否跟随外容器自动缩放
   * @default false
   */
  resizable?: boolean;
  /**
   * @description x轴数据 更多配置见 https://echarts.apache.org/zh/option.html#xAxis
   */
  xAxisData: string[];
  /**
   * @description y轴数据 更多配置见 https://echarts.apache.org/zh/option.html#series-bar
   */
  seriesData: any[];
  /**
   * @description echarts更多配置 详情见https://echarts.apache.org/zh/option.html#title
   * @type EChartsOption
   */
  config?: EChartsOption;
  /**
   * @description 图表外层包装容器的类名
   */
  wrapperClassName?: string;
  /**
   * 突破组件内部写死的options配置时使用，使用时可完全外部自定义所有的图表的配置项
   */
  breakthroughOptionsConfig?: any;
  /**
   * 突破组件内部写死的series配置时使用，使用时可自定义配置series中的部分参数
   */
  breakthroughSeriesConfig?: any;
  /**
   * @description 是否同步引用数据到父级（xAxisData、seriesData），一般在 direction='horizontal' 的时候使用，此时内部对数据进行倒序，如果外部需要使用同步使用倒序后的数据，那么就设置为 true
   * @default false
   */
  isSyncData?: boolean;
}

/**
* @description 便捷文档地址
* @see https://reactpc.lanhanba.net/components/charts/v2bar-chart
*/

const V2BarChart: React.FC<V2BarChartProps> = (props) => {
  const {
    title,
    theme = 'blue',
    customTheme,
    width,
    height = 270,
    direction = 'vertical',
    resizable = false,
    xAxisData = [],
    seriesData = [],
    config = {},
    wrapperClassName,
    border = false,
    breakthroughOptionsConfig,
    breakthroughSeriesConfig,
    isSyncData = false,
  } = props;
  const chartRef = useRef<any>(null);
  const [echartsInstance, setEchartsInstance] = useState<any>(null);

  const seriesBaseConfig = {
    type: 'bar',
    smooth: true,
    showSymbol: false,
    barMaxWidth: 11,
    itemStyle: {},
    label: {
      show: false,
      position: 'top',
      color: '#999999',
      fontSize: 10,
      formatter: (obj: any) => yAxisFormatter(obj.data)
    },
  };

  const seriesAreaStyleConfig = (index: number) => {
    return {
      color: {
        type: 'linear',
        x: direction === 'vertical' ? 0 : 1,
        y: 0,
        x2: 0,
        y2: direction === 'vertical' ? 1 : 0,
        colorStops: [{
          offset: 0, color: customTheme?.color?.[index] || themeColor[theme][index],
        }, {
          offset: 1, color: `rgba(${customTheme?.colorRGB?.[index] || themeColor[`${theme}RGB`][index]}, 0.5)`, // 50%透明度
        }],
        global: false,
      },
    };
  };

  // 横轴配置
  const xAxis = {
    type: 'category',
    data: [],
    nameTextStyle: {
      fontSize: 13,
      fontFamily: 'PingFangSC-Regular, PingFang SC',
    },
    axisLine: {
      show: false,
      lineStyle: {
        color: '#999',
      },
    },
    axisTick: {
      show: false,
    },
    ...(config.xAxis || {}),
  };

  // 竖轴配置
  const yAxis = {
    type: 'value',
    axisLabel: {
      formatter: yAxisFormatter,
    },
    nameTextStyle: {
      fontSize: 13,
      fontFamily: 'PingFangSC-Regular, PingFang SC',
    },
    axisLine: {
      lineStyle: {
        color: '#999',
      },
    },
    splitLine: {
      lineStyle: {
        color: ['#eee'],
        width: 0.5,
      },
    },
    ...(config.yAxis || {}),
  };

  const [isConfig, setIsConfig] = useState(false); // options 是否配置完成
  const [options, setOptions] = useState<any>({
    ...config,
    title: {
      show: !!title,
      text: title || '',
      textStyle: {
        color: '#222222',
        fontWeight: 500,
        fontFamily: 'PingFangSC-Medium, PingFang SC',
        fontSize: 16,
        lineHeight: 22,
      },
      top: 6,
      left: 6,
      ...(config.title || {}),
    },
    legend: {
      type: 'plain',
      width: '50%',
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 2,
      itemStyle: {
        borderWidth: 0,
      },
      textStyle: {
        fontSize: 13,
        fontWeight: 400,
        color: '#666666',
        lineHeight: 14,
        rich: {
          a: {
            verticalAlign: 'middle',
          },
        },
        padding: [0, 4, 1, 0],
      },
      icon: 'roundRect',
      top: 10,
      right: 0,
      ...(config.legend || {}),
    },
    color: customTheme?.color || themeColor[theme],
    grid: {
      left: 50,
      bottom: 36,
      right: 12,
      ...(config.grid || {}),
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: '#222',
      borderColor: '#222',
      borderWidth: 0,
      textStyle: {
        color: '#fff',
        fontWeight: 'normal',
        fontSize: 12,
        fontFamily: 'PingFangSC-Regular, PingFang SC',
      },
      confine: true,
      alwaysShowContent: false,
      ...(config.tooltip || {}),
    },
    xAxis: direction === 'vertical' ? xAxis : yAxis,
    yAxis: direction === 'vertical' ? yAxis : xAxis,
    series: [],
  });

  useEffect(() => {

    if (Array.isArray(xAxisData) && xAxisData?.length && Array.isArray(seriesData) && seriesData?.length) {

      // 如果传入参数同步引用数据到父级，那么就不使用 deepCopy
      const beforeReverse = isSyncData ? (data) => data : (data) => deepCopy(data);

      const _xAxisData = direction === 'vertical' ? xAxisData : beforeReverse(xAxisData).reverse();
      const _seriesData = direction === 'vertical' ? seriesData : seriesData.map(item => ({ ...item, data: beforeReverse(item.data).reverse() }));

      const direct = direction === 'vertical' ? 'xAxis' : 'yAxis';
      unstable_batchedUpdates(() => {
        setOptions({
          ...options,
          series: _seriesData.map((item, index) => {
            return {
              ...seriesBaseConfig,
              ...item,
              data: item.data,
              itemStyle: item.itemStyle ? item.itemStyle : seriesAreaStyleConfig(index),
              tooltip: {
                valueFormatter: (value: number | string | null | undefined) => {
                  if (value === null || value === undefined || value === '') return '--';
                  return item.unit ? `${value}${item.unit}` : value;
                }
              },
              label: {
                ...seriesBaseConfig.label,
                ...item.label || {},
              },
              // 原本的代码逻辑就不支持在组件外部自定义某些参数，现在加上可以重置组件内部配置的参数，注意使用时一定要搞清楚再用。为了不影响既往的功能
              ...(isObject(breakthroughSeriesConfig) ? breakthroughSeriesConfig : {})
            };
          }),
          [direct]: {
            ...options[direct],
            data: _xAxisData,
          },
          ...(isObject(breakthroughOptionsConfig) ? breakthroughOptionsConfig : {})
        });
        setIsConfig(true);
      });
    }
  }, [xAxisData, seriesData]);

  useEffect(() => {
    if (!isConfig) return;
    let chart: any;
    if (chartRef.current) {
      chart = echarts.init(chartRef.current, 'light', {
        renderer: 'svg',
        height,
      });
      chart.setOption(options);
      setEchartsInstance(chart);
    }
    return () => chart?.dispose();
  }, [options, isConfig]);

  useEffect(() => {
    if (echartsInstance && resizable) {
      const resizeHandler = throttle(() => {
        if (!echartsInstance.isDisposed()) {
          echartsInstance.resize(); // 图表根据窗口大小自适应
        }
      }, 300);
      window.addEventListener('resize', resizeHandler);
      return () => {
        window.removeEventListener('resize', resizeHandler);
      };
    }
    return undefined;
  }, [echartsInstance, resizable]);

  return (
    <div className={cs(styles.V2BarChartWrap, wrapperClassName)} style={{ width, height }}>
      <div ref={chartRef} className={cs([styles.V2BarChart, 'V2BarChart', border && styles.V2BarChartBorder])}></div>
    </div>
  );
};

export default V2BarChart;
